import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { ConversationResolutionSentiment } from "@redotech/redo-support-common/conversation-insights";
import { DateSchema } from "@redotech/redo-support-common/conversation/shared-schemas";
import { ConversationPlatform } from "@redotech/redo-support-conversation-protocol-common/conversation-protocol";
import {
  CsatSurveyMethod,
  SurveySendTimeOptions,
} from "@redotech/redo-support-settings-common/csat-survey";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import {
  CONVERSATION_STATUSES_V3,
  MESSAGE_CHANNELS_V3,
  TimeUnit,
} from "../rules-v3/types";
import {
  AssignV3Strategy,
  SetStatusActionStatus,
  SetStatusSnoozeDuration,
  SetStatusSnoozePeriod,
} from "./actions";
import {
  DateQueryOperator,
  dateQueryOperators,
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
  selectionQueryOperators,
  textQueryOperators,
} from "./types";

export enum AssignmentTypeOption {
  AI_AGENT_ASSIGNEE = "AI agent assigned",
  SUPPORT_AGENT_ASSIGNEE = "Support agent assigned",
  NO_ASSIGNEE = "No assignee",
}

export enum TicketStatusOption {
  OPEN = "open",
  CLOSED = "closed",
  SNOOZED = "snoozed",
  IN_PROGRESS = "in_progress",
}

export enum SubjectLineMatchType {
  EXACT = "exact",
  CONTAINS = "contains",
}

// Base step schema
const baseStepSchema = z.object({
  customTitle: z.string().optional(),
  type: createEnum(StepType),
  id: z.string(),
});

/* Trigger Metadata Schemas */

/**
 * The actor that fired the trigger, when one can be identified. Currently
 * populated by message-related triggers from the message's `senderType` /
 * `senderMongoUserId`. `null` / omitted for triggers that have no meaningful
 * actor (tag added, status changed by another rule, etc.).
 */
const triggerActorSchema_ = z
  .object({ assigneeType: z.enum(["USER", "AI"]), mongoUserId: z.string() })
  .nullish();
export const triggerActorSchema: z.ZodType<TriggerActor> = triggerActorSchema_;
export type TriggerActor = z.infer<typeof triggerActorSchema_>;

const triggerMetadataSchema_ = z.object({
  triggerType: createEnum(RuleTriggerType),
  triggeredAt: DateSchema,
  subjectEntityId: z.string(),
  triggerDetails: z.record(z.string(), z.unknown()),
  actor: triggerActorSchema_,
});
export const triggerMetadataSchema: z.ZodType<TriggerMetadata> =
  triggerMetadataSchema_;
export type TriggerMetadata = z.infer<typeof triggerMetadataSchema_>;

/* Condition Group Schemas for AND/OR Logic */

const conditionGroupOperator = z.enum(["AND", "OR"]);

export type ConditionGroupOperator = z.infer<typeof conditionGroupOperator>;

/* Rule Condition Schemas */

const ticketChannelConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.TICKET_CHANNEL),
  channels: z.array(createEnum(ConversationPlatform)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const ticketChannelConditionSchema: z.ZodType<TicketChannelCondition> =
  ticketChannelConditionSchema_;
export interface TicketChannelCondition extends z.infer<
  typeof ticketChannelConditionSchema_
> {}

/**
 * Topic-condition mode.
 *
 * - "default" — matches against the global, Redo-curated topic set (the legacy
 *   path, kept as the default so existing rules without `mode` continue to work
 *   unchanged). `topics` is the user's selection from that global set, by name.
 * - "custom" — matches against the merchant's own topic catalog defined in the
 *   support-v2 Topics setting. `customTopicIds` is the user's selection from
 *   that per-team catalog, by `SupportTicketMerchantTopicId`.
 *
 * Both selection arrays are kept on the same node so a node can switch modes
 * without losing prior selections — the evaluator only reads the array that
 * corresponds to the active mode.
 */
export const TopicConditionMode = {
  DEFAULT: "default",
  CUSTOM: "custom",
} as const;
export type TopicConditionMode =
  (typeof TopicConditionMode)[keyof typeof TopicConditionMode];

const topicConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.TOPIC),
  // Optional rather than defaulted so existing call sites that construct a
  // `TopicCondition` literal without `mode` (legacy migrator + the rules-v2/v3
  // UIs) stay type-correct. The handler treats `undefined` as `default`.
  mode: z
    .enum([TopicConditionMode.DEFAULT, TopicConditionMode.CUSTOM])
    .optional(),
  topics: z.array(z.string()).optional(),
  customTopicIds: z.array(z.string()).optional(),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const topicConditionSchema: z.ZodType<TopicCondition> =
  topicConditionSchema_;
export interface TopicCondition extends z.infer<typeof topicConditionSchema_> {}

const sentFromEmailConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.SENT_FROM_EMAIL),
  emails: z.array(z.string()).optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.CONTAINS),
});
export const sentFromEmailConditionSchema: z.ZodType<SentFromEmailCondition> =
  sentFromEmailConditionSchema_;
export interface SentFromEmailCondition extends z.infer<
  typeof sentFromEmailConditionSchema_
> {}

const receivedAtEmailConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.RECEIVED_AT_EMAIL),
  emails: z.array(z.string()).optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.IS_EXACTLY),
});
export const receivedAtEmailConditionSchema: z.ZodType<ReceivedAtEmailCondition> =
  receivedAtEmailConditionSchema_;
export interface ReceivedAtEmailCondition extends z.infer<
  typeof receivedAtEmailConditionSchema_
> {}

const subjectLineConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.SUBJECT_LINE),
  fuzzy_match: z.string().optional(),
  queryStrings: z.array(z.string()).optional(),
  caseSensitive: z.boolean().optional(),
  /**
   * When true, "contains" matches raw substrings (e.g. "order" also matches
   * "orders"/"reorder"). When false/undefined (default), it matches whole words
   * only.
   */
  allowSubstring: z.boolean().optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.CONTAINS),
});
export const subjectLineConditionSchema: z.ZodType<SubjectLineCondition> =
  subjectLineConditionSchema_;
export interface SubjectLineCondition extends z.infer<
  typeof subjectLineConditionSchema_
> {}

const messageBodyMatchesConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.MESSAGE_BODY_MATCHES),
  queryString: z.string().optional(),
  queryStrings: z.array(z.string()).optional(),
  caseSensitive: z.boolean().default(false),
  /**
   * When true, "contains" matches raw substrings (e.g. "order" also matches
   * "orders"/"reorder"). When false/undefined (default), it matches whole words
   * only.
   */
  allowSubstring: z.boolean().optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.CONTAINS),
});
export const messageBodyMatchesConditionSchema: z.ZodType<MessageBodyMatchesCondition> =
  messageBodyMatchesConditionSchema_;
export interface MessageBodyMatchesCondition extends z.infer<
  typeof messageBodyMatchesConditionSchema_
> {}

const afterHoursConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.AFTER_HOURS),
});
export const afterHoursConditionSchema: z.ZodType<AfterHoursCondition> =
  afterHoursConditionSchema_;
export interface AfterHoursCondition extends z.infer<
  typeof afterHoursConditionSchema_
> {}

const assignmentTypeConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.ASSIGNMENT_TYPE),
  assignmentType: createEnum(AssignmentTypeOption).default(
    AssignmentTypeOption.SUPPORT_AGENT_ASSIGNEE,
  ),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const assignmentTypeConditionSchema: z.ZodType<AssignmentTypeCondition> =
  assignmentTypeConditionSchema_;
export interface AssignmentTypeCondition extends z.infer<
  typeof assignmentTypeConditionSchema_
> {}

const lastOrderTimeConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.LAST_ORDER_TIME),
  queryOperator: z
    .enum(dateQueryOperators)
    .default(DateQueryOperator.WITHIN_LAST),
  days: z.number().optional(),
  date: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});
export const lastOrderTimeConditionSchema: z.ZodType<LastOrderTimeCondition> =
  lastOrderTimeConditionSchema_;
export interface LastOrderTimeCondition extends z.infer<
  typeof lastOrderTimeConditionSchema_
> {}

const messageSentimentConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.MESSAGE_SENTIMENT),
  sentiments: z.array(createEnum(ConversationResolutionSentiment)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const messageSentimentConditionSchema: z.ZodType<MessageSentimentCondition> =
  messageSentimentConditionSchema_;
export interface MessageSentimentCondition extends z.infer<
  typeof messageSentimentConditionSchema_
> {}

const ticketStatusConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.TICKET_STATUS),
  statuses: z.array(createEnum(TicketStatusOption)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const ticketStatusConditionSchema: z.ZodType<TicketStatusCondition> =
  ticketStatusConditionSchema_;
export interface TicketStatusCondition extends z.infer<
  typeof ticketStatusConditionSchema_
> {}

const hasTagConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.HAS_TAG),
  tags: z.array(z.string()).optional(),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const hasTagConditionSchema: z.ZodType<HasTagCondition> =
  hasTagConditionSchema_;
export interface HasTagCondition extends z.infer<
  typeof hasTagConditionSchema_
> {}

const triggerTagConditionSchema_ = z.object({
  conditionType: z.literal(RuleConditionType.TRIGGER_TAG),
  tags: z.array(z.string()).optional(),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});
export const triggerTagConditionSchema: z.ZodType<TriggerTagCondition> =
  triggerTagConditionSchema_;
export interface TriggerTagCondition extends z.infer<
  typeof triggerTagConditionSchema_
> {}

const ruleConditionSchema_ = z.union([
  ticketChannelConditionSchema_,
  ticketStatusConditionSchema_,
  topicConditionSchema_,
  sentFromEmailConditionSchema_,
  receivedAtEmailConditionSchema_,
  subjectLineConditionSchema_,
  messageBodyMatchesConditionSchema_,
  afterHoursConditionSchema_,
  assignmentTypeConditionSchema_,
  lastOrderTimeConditionSchema_,
  messageSentimentConditionSchema_,
  hasTagConditionSchema_,
]);
export const ruleConditionSchema: z.ZodType<RuleCondition> =
  ruleConditionSchema_;
export type RuleCondition = z.infer<typeof ruleConditionSchema_>;

const conditionGroupSchema_: z.ZodType<ConditionGroup> = z.lazy(() =>
  z.object({
    operator: conditionGroupOperator,
    conditions: z.array(z.union([ruleConditionSchema_, conditionGroupSchema_])),
  }),
);

export const conditionGroupSchema: z.ZodType<ConditionGroup> =
  conditionGroupSchema_;

export interface ConditionGroup {
  operator: ConditionGroupOperator;
  conditions: (RuleCondition | ConditionGroup)[];
}

/* Rule Action Schemas */

const setStatusActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.SET_STATUS),
  status: createEnum(SetStatusActionStatus),
  snoozePeriod: createEnum(SetStatusSnoozePeriod).optional(),
});
export const setStatusActionSchema: z.ZodType<SetStatusAction> =
  setStatusActionSchema_;
export interface SetStatusAction extends z.infer<
  typeof setStatusActionSchema_
> {}

const addTagActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.ADD_TAG),
  tag: z.array(z.string()),
});
export const addTagActionSchema: z.ZodType<AddTagAction> = addTagActionSchema_;
export interface AddTagAction extends z.infer<typeof addTagActionSchema_> {}

const removeTagActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.REMOVE_TAG),
  tag: z.array(z.string()),
});
export const removeTagActionSchema: z.ZodType<RemoveTagAction> =
  removeTagActionSchema_;
export interface RemoveTagAction extends z.infer<
  typeof removeTagActionSchema_
> {}

const autoReplyActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.AUTO_REPLY),
  textContent: z.string(),
  htmlContent: z.string(),
  attachments: z
    .array(
      z.object({
        url: z.string(),
        description: z.string(),
        mimeType: z.string(),
      }),
    )
    .optional(),
  signatureSeeded: z.boolean().optional(),
});
export const autoReplyActionSchema: z.ZodType<AutoReplyAction> =
  autoReplyActionSchema_;
export interface AutoReplyAction extends z.infer<
  typeof autoReplyActionSchema_
> {}

const assignActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.ASSIGN),
  user: z.object({ id: z.string(), name: z.string() }),
});
export const assignActionSchema: z.ZodType<AssignAction> = assignActionSchema_;
export interface AssignAction extends z.infer<typeof assignActionSchema_> {}

const assignModeSchema = z.discriminatedUnion("_tag", [
  z.object({
    _tag: z.literal("specific_users"),
    mongoUserIds: z.array(z.string()),
  }),
  z.object({ _tag: z.literal("user_group"), userGroupId: z.string() }),
  z.object({ _tag: z.literal("unassign") }),
  z.object({
    _tag: z.literal("actor"),
    assigneeTypes: z.array(z.enum(["USER", "AI"])).min(1),
  }),
  z.object({ _tag: z.literal("ai") }),
]);

const assignV3ActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.ASSIGN_V3),
  mode: assignModeSchema,
  strategy: createEnum(AssignV3Strategy),
  // Optional per-user open-ticket cap. Null / omitted = no cap.
  maxTicketsPerUser: z.number().int().positive().nullable().optional(),
});
export const assignV3ActionSchema: z.ZodType<AssignV3Action> =
  assignV3ActionSchema_;
export interface AssignV3Action extends z.infer<typeof assignV3ActionSchema_> {}

const assignOldestTicketActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.ASSIGN_OLDEST_TICKET),
  mode: assignModeSchema,
  strategy: createEnum(AssignV3Strategy),
  maxTicketsPerUser: z.number().int().positive().nullable().optional(),
});
export const assignOldestTicketActionSchema: z.ZodType<AssignOldestTicketAction> =
  assignOldestTicketActionSchema_;
export interface AssignOldestTicketAction extends z.infer<
  typeof assignOldestTicketActionSchema_
> {}

const likeCommentActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.LIKE_COMMENT),
});
export const likeCommentActionSchema: z.ZodType<LikeCommentAction> =
  likeCommentActionSchema_;
export interface LikeCommentAction extends z.infer<
  typeof likeCommentActionSchema_
> {}

const hideCommentActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.HIDE_COMMENT),
});
export const hideCommentActionSchema: z.ZodType<HideCommentAction> =
  hideCommentActionSchema_;
export interface HideCommentAction extends z.infer<
  typeof hideCommentActionSchema_
> {}

const setPriorityActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.SET_PRIORITY),
  // `null` clears the priority. `"URGENT"` is currently the only set value.
  priority: z.enum(["URGENT"]).nullable(),
});
export const setPriorityActionSchema: z.ZodType<SetPriorityAction> =
  setPriorityActionSchema_;
export interface SetPriorityAction extends z.infer<
  typeof setPriorityActionSchema_
> {}

const setStatusV3ActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.SET_STATUS_V3),
  status: z.enum([
    "OPEN",
    "CLOSED",
    "SNOOZED",
    "IN_PROGRESS",
    "ARCHIVED",
    "DELETED",
  ]),
  snoozeDuration: createEnum(SetStatusSnoozeDuration).optional(),
});
export const setStatusV3ActionSchema: z.ZodType<SetStatusV3Action> =
  setStatusV3ActionSchema_;
export interface SetStatusV3Action extends z.infer<
  typeof setStatusV3ActionSchema_
> {}

const startAiAgentExecutionActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.START_AI_AGENT_EXECUTION),
});
export const startAiAgentExecutionActionSchema: z.ZodType<StartAiAgentExecutionAction> =
  startAiAgentExecutionActionSchema_;
export interface StartAiAgentExecutionAction extends z.infer<
  typeof startAiAgentExecutionActionSchema_
> {}

const handoffAiActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.HANDOFF_AI),
});
export const handoffAiActionSchema: z.ZodType<HandoffAiAction> =
  handoffAiActionSchema_;
export interface HandoffAiAction extends z.infer<
  typeof handoffAiActionSchema_
> {}

const generateConversationInsightsActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.GENERATE_CONVERSATION_INSIGHTS),
});
export const generateConversationInsightsActionSchema: z.ZodType<GenerateConversationInsightsAction> =
  generateConversationInsightsActionSchema_;
export interface GenerateConversationInsightsAction extends z.infer<
  typeof generateConversationInsightsActionSchema_
> {}

const generateSuggestedMerchantKnowledgeActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.GENERATE_SUGGESTED_MERCHANT_KNOWLEDGE),
});
export const generateSuggestedMerchantKnowledgeActionSchema: z.ZodType<GenerateSuggestedMerchantKnowledgeAction> =
  generateSuggestedMerchantKnowledgeActionSchema_;
export interface GenerateSuggestedMerchantKnowledgeAction extends z.infer<
  typeof generateSuggestedMerchantKnowledgeActionSchema_
> {}

const sendCsatSurveyActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.SEND_CSAT_SURVEY),
  surveySendTime: createEnum(SurveySendTimeOptions).optional(),
  surveyMethods: z.array(createEnum(CsatSurveyMethod)).optional(),
  allowRemarks: z.boolean().optional(),
  headerImage: z.string().optional(),
  surveyMessage: z.string().optional(),
});
export const sendCsatSurveyActionSchema: z.ZodType<SendCsatSurveyAction> =
  sendCsatSurveyActionSchema_;
export interface SendCsatSurveyAction extends z.infer<
  typeof sendCsatSurveyActionSchema_
> {}

const mergeSupportTicketActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.MERGE_SUPPORT_TICKET),
  // Required — at least one status must be selected, otherwise no peer
  // ticket can ever match and the action is a guaranteed no-op.
  allowedStatuses: z.array(z.enum(CONVERSATION_STATUSES_V3)).min(1),
  maxAgeSinceLastMessage: z.object({
    value: z.number().int().positive(),
    unit: z.nativeEnum(TimeUnit),
  }),
});
export const mergeSupportTicketActionSchema: z.ZodType<MergeSupportTicketAction> =
  mergeSupportTicketActionSchema_;
export interface MergeSupportTicketAction extends z.infer<
  typeof mergeSupportTicketActionSchema_
> {}

const splitSupportTicketActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.SPLIT_SUPPORT_TICKET),
  routeFutureMessagesToNewTicket: z.boolean().default(true),
});
export const splitSupportTicketActionSchema: z.ZodType<SplitSupportTicketAction> =
  splitSupportTicketActionSchema_;
export interface SplitSupportTicketAction extends z.infer<
  typeof splitSupportTicketActionSchema_
> {}

const stopTopicContinuationActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.STOP_TOPIC_CONTINUATION),
  channels: z.array(z.enum(MESSAGE_CHANNELS_V3)).optional(),
});
export const stopTopicContinuationActionSchema: z.ZodType<StopTopicContinuationAction> =
  stopTopicContinuationActionSchema_;
export interface StopTopicContinuationAction extends z.infer<
  typeof stopTopicContinuationActionSchema_
> {}

const runCustomCodeActionSchema_ = z.object({
  actionType: z.literal(RuleActionType.RUN_CUSTOM_CODE),
  codeArtifactId: z.string(),
});
export const runCustomCodeActionSchema: z.ZodType<RunCustomCodeAction> =
  runCustomCodeActionSchema_;
export interface RunCustomCodeAction extends z.infer<
  typeof runCustomCodeActionSchema_
> {}

const ruleActionSchema_ = z.union([
  setStatusActionSchema_,
  addTagActionSchema_,
  removeTagActionSchema_,
  autoReplyActionSchema_,
  assignActionSchema_,
  assignV3ActionSchema_,
  assignOldestTicketActionSchema_,
  likeCommentActionSchema_,
  hideCommentActionSchema_,
  setPriorityActionSchema_,
  setStatusV3ActionSchema_,
  startAiAgentExecutionActionSchema_,
  handoffAiActionSchema_,
  generateConversationInsightsActionSchema_,
  generateSuggestedMerchantKnowledgeActionSchema_,
  sendCsatSurveyActionSchema_,
  mergeSupportTicketActionSchema_,
  splitSupportTicketActionSchema_,
  stopTopicContinuationActionSchema_,
  runCustomCodeActionSchema_,
]);
export const ruleActionSchema: z.ZodType<RuleAction> = ruleActionSchema_;
export type RuleAction = z.infer<typeof ruleActionSchema_>;

/* Rule Step Schemas */

const ruleConditionsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RULE_CONDITIONS),
  conditionGroups: conditionGroupSchema,
  nextTrueId: z.string().optional(),
  nextFalseId: z.string().optional(),
});
export const ruleConditionsStepSchema: z.ZodType<RuleConditionsStep> =
  ruleConditionsStepSchema_;
export interface RuleConditionsStep extends z.infer<
  typeof ruleConditionsStepSchema_
> {}

const ruleActionsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RULE_ACTIONS),
  actions: z.array(ruleActionSchema_),
  errorHandlingStrategy: createEnum(ErrorHandlingStrategy).default(
    ErrorHandlingStrategy.FAIL_FAST,
  ),
  nextId: z.string().optional(),
});
export const ruleActionsStepSchema: z.ZodType<RuleActionsStep> =
  ruleActionsStepSchema_;
export interface RuleActionsStep extends z.infer<
  typeof ruleActionsStepSchema_
> {}

/* Rule Execution Metadata Schemas */

const conditionResultSchema_ = z.object({
  conditionType: createEnum(RuleConditionType),
  passed: z.boolean(),
  evaluatedAt: DateSchema,
  error: z.string().optional(),
});
export const conditionResultSchema: z.ZodType<ConditionResult> =
  conditionResultSchema_;
export interface ConditionResult extends z.infer<
  typeof conditionResultSchema_
> {}

const actionResultSchema_ = z.object({
  actionType: createEnum(RuleActionType),
  status: z.enum(["success", "failed", "skipped"]),
  executedAt: DateSchema,
  error: z.string().optional(),
});
export const actionResultSchema: z.ZodType<ActionResult> = actionResultSchema_;
export interface ActionResult extends z.infer<typeof actionResultSchema_> {}

const ruleExecutionMetadataSchema_ = z.object({
  trigger: triggerMetadataSchema_,
  conditionResults: z.array(conditionResultSchema_).optional(),
  actionResults: z.array(actionResultSchema_).optional(),
  executionId: z.string(),
  startedAt: DateSchema,
  completedAt: DateSchema.optional(),
  status: z.enum(["success", "failed", "partial"]),
});
export const ruleExecutionMetadataSchema: z.ZodType<RuleExecutionMetadata> =
  ruleExecutionMetadataSchema_;
export interface RuleExecutionMetadata extends z.infer<
  typeof ruleExecutionMetadataSchema_
> {}
